import React, { useState, useEffect, useRef, ReactElement } from 'react';
import { uniqueNamesGenerator, names } from 'unique-names-generator';
import moment from 'moment';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Settings as SettingsIcon, PersonAdd as PersonAddIcon, Lock as LockIcon, LockOpen as LockOpenIcon, SwapHoriz, ArrowDropDown as ArrowDropDownIcon, ArrowDownward, ArrowUpward, Schedule } from '@mui/icons-material'
import {
  AppBar, IconButton, MenuItem, MenuList, Toolbar, Typography,
  Switch, FormControlLabel, Dialog,
  Button, TextField, InputAdornment, Divider, Tooltip, Menu, Snackbar, Alert
} from '@mui/material';
import { CookieSetOptions } from 'universal-cookie';


import './Header.css';
import { listSeriesSessions } from '../../meeting/v2Api';
import { SessionReplay } from '../SessionReplay/SessionReplay';
import { SelfSubmit } from './SelfSubmit/SelfSubmit';
import { MIN_PRESENTER_TIME, getMinimumMeetingLengthSeconds } from '../../TimeHelper';



const headerTheme = createTheme({
  palette: {
    primary: {
      main: '#171717',
    },
    secondary: {
      main: '#E6E6E6',
    },
  },
});

const TIME_DISPLAY_FORMAT = "HH:mm";

interface HeaderProps {

  cookies: any; /** user identifying cookie */
  cursor: number; /** The index of the current presenter (in relation to presenters) */
  elapsedTimeTrigger: boolean /** Toggle that flips every second */
  isAdmin: boolean; /** Whether or not app is in admin view */
  isArchived: boolean;   /** Is this presentation part of a meeting that has been archived */
  isDebugModeEnabled: boolean;  /** Whether or not debug mode is on */

  meetingName: string; /** Meeting name */
  meetingLength: number; /** The scheduled meeting length in seconds */
  meetingID: string; /** The meeting ID for the database (based on meeting name) */
  meetingEndTime: Date; /** The end time of the meeting, as Date object */
  meetingStartTime: Date; /** The start time of the meeting, as Date object */

  presenters: Presenter[]; /** A list of all the presenters */
  presenterOrder: string[]; /** An array of strings that indacates the order of presenters */

  restartMeeting: () => Promise<void>;
  clearMeetingAgenda: Function;

  setCookie: (name: "user", value: any, options?: CookieSetOptions | undefined) => void; /** Function that updates user cookie */

  setStartMeeting: (state: boolean) => void; /** Function that toggles the state of the start button */
  setIsNextButtonPressed: (state: boolean) => void; /** Function that toggles the state of the next presenter button */
  setIsPrevButtonPressed: (state: boolean) => void; /** Function that toggles the state of the previous presenter button */
  updateStateFromDatabase: (newMeetingID?: string) => Promise<void>; /** Fetches the state from database / syncs local state to shared state */
  writeMeetingChange: (valuesToUpdate: StateUpdateObject) => Promise<boolean>; /** Function that writes changes to the db (and locally) */



  errorMsg: string;
  setErrorMsg: Function;
  errorVisible: boolean;
  setErrorVisible: Function;
  setPresenters: Function;
  setMEETING_ID: Function;

  archiveConfirmation: boolean;
  setArchiveConfirmation: Function;
  archiveMeeting: Function;
  prepareNextSession: () => Promise<void>;
  viewingSessionId: string | null;
  loadHistoricalSession: (sessionId: string) => Promise<void>;
  loadLiveSession: () => Promise<void>;
  liveSessionId: string;
  meetingEditsEnabled: boolean;

  setLoading: Function;
  setIsAdmin: Function;
  bonusTime: Presenter;
}

let archivedMeetingList: { sessionId: string; endedAt: string }[] = [];



export const Header = (props: HeaderProps): ReactElement => {
  const { meetingName, meetingLength, meetingEndTime, isAdmin, setStartMeeting, setIsNextButtonPressed, setIsPrevButtonPressed,
    cursor, elapsedTimeTrigger, writeMeetingChange, restartMeeting: handleMeetingReset,
    presenters, updateStateFromDatabase, isDebugModeEnabled, cookies, setCookie, setMEETING_ID,
    isArchived, setLoading, setIsAdmin, archiveMeeting, clearMeetingAgenda, bonusTime, presenterOrder,
    prepareNextSession, viewingSessionId, loadHistoricalSession, loadLiveSession, liveSessionId, meetingID,
    meetingStartTime, meetingEditsEnabled,
   } = props;


  const [tempMeetingLength, setTempMeetingLength] = useState(meetingLength)
  const [tempEndTime, setTempEndTime] = useState((moment().add(meetingLength, "s")).format(TIME_DISPLAY_FORMAT))
  const [addPresenterPopup, setAddPresenterPopup] = useState(false)
  const [isEditing, setIsEditing] = useState(false);

  const [archiveMenu, setArchiveMenu] = useState<null | HTMLElement>(null);
  const [replayOpen, setReplayOpen] = useState(false);
  const [prepareConfirmOpen, setPrepareConfirmOpen] = useState(false);
  const [settingsMenu, setSettingsMenu] = useState<null | HTMLElement>(null);

  const [prioritizeEndTime, setIsEditable] = useState(false);
  const [scheduleToastOpen, setScheduleToastOpen] = useState(false);
  const [scheduleToastMessage, setScheduleToastMessage] = useState('');

  const settingsAnchorRef = useRef(null);
  const archiveAnchorRef = useRef(null);


  // Archive =============================================================================

  const retrieveArchivedList = async () => {
    try {
      archivedMeetingList = await listSeriesSessions(meetingID);
    } catch (err) {
      console.log("Failed to load session history", err);
    }
  };

  useEffect(() => {
    if (archiveMenu) {
      retrieveArchivedList();
    }
  }, [archiveMenu, meetingID]);
  // =====================================================================================


  // Debug Mode ==========================================================================

  const handleDebug = (): void => {
    if (cursor < 0) {
      writeMeetingChange({ debugStatus: !isDebugModeEnabled });
    }
  }
  // =====================================================================================


  // Random Meeting ======================================================================

  function seperateIntoRandomParts(total: number, numberOfParts: number, minSize: number): number[] {
    if (numberOfParts <= 0) {
      return [];
    }
    let parts = numberOfParts;
    if (total < minSize * parts) {
      parts = Math.max(1, Math.floor(total / minSize));
    }
    const randombit = total - minSize * parts;
    const out: number[] = [];
    for (let i = 0; i < parts; i++) {
      out.push(Math.random());
    }
    const weightSum = out.reduce((a, b) => a + b, 0);
    const mult = weightSum > 0 ? randombit / weightSum : 0;
    return out.map((el) => Math.max(minSize, Math.round(el * mult + minSize)));
  }

  const handleCreateRandomMeeting = async () => {
    const descriptionPool = [
      "The Future of Transportation: Autonomous Cars",
      "Maximizing Productivity with Time Management Techniques" ,
      "The Benefits of a Plant-Based Diet",
      "Introduction to Data Science",
      "The Power of Positive Thinking",
      "Effective Communication Skills for the Workplace",
      "Exploring the World of Artificial Intelligence",
      "Sustainable Agriculture Practices for the 21st Century",
      "The Importance of Diversity and Inclusion",
      "Navigating Career Changes",
      "The Science of Happiness",
      "The Future of Work: Remote and Hybrid Models",
      "The Art of Public Speaking",
      "Mastering Social Media Marketing",
      "Innovative Problem-Solving Strategies",
      "The Role of Emotional Intelligence in Leadership",
      "Exploring the World of Virtual Reality",
      "The Power of Collaboration",
      "The Science of Sleep and Its Importance",
      "Understanding Cryptocurrencies"
    ]



    await handleMeetingReset();
    await clearMeetingAgenda();

    const totalMinutes = Math.max(1, meetingLength / 60);
    const maxPresenterCount = Math.max(1, Math.min(Math.round(totalMinutes / 2), 15));
    const randomPresenterCount = Math.floor(Math.random() * maxPresenterCount) + 1;
    const budgetMinutes = totalMinutes * (Math.random() * 0.8 + 0.2);
    const randomTimes = seperateIntoRandomParts(budgetMinutes, randomPresenterCount, 1);
    let randomIDComplexity = 1000000000

    let randomPresenters:Presenter[] = [];

    for (let i = 0; i < randomPresenterCount; i++) {
      randomPresenters.push({
        id: "PARTICIPANT#" + Math.floor(Math.random() * randomIDComplexity),
        name: uniqueNamesGenerator({ dictionaries: [names] }),
        description: descriptionPool[Math.round(Math.random() * (descriptionPool.length - 1))],
        flight: 3,
        initialDuration: randomTimes[i]*60,
        currentDuration: randomTimes[i]*60,
        elapsedTime: 0,
        dbElapsedTime: 0,
        startTime: "",
        endTime: "",
        priority: Math.round(Math.random() * 2),
        slotType: 'Presenter'
      })
      
    }
    writeMeetingChange({ presenterAdd: randomPresenters });

  }
  // =====================================================================================

  // Shuffle Order =======================================================================

  const handleMeetingShuffle = () => {
    writeMeetingChange({ presenterOrder: shufflePresenters(presenterOrder) })
  }

  const shufflePresenters = (array: string[]): string[] => {
    let currentIndex = array.length, randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex], array[currentIndex]];
    }
    return array;
  }
  // =====================================================================================

  // Meeting Length ======================================================================


  const scheduleCursor = (): number => (sessionHasStarted() ? cursor : -1);

  const calculateMinimumMeetingLength = (): number =>
    getMinimumMeetingLengthSeconds(presenters, scheduleCursor());

  const getEarliestAllowedEndTime = (): moment.Moment => {
    const minTotalSeconds = calculateMinimumMeetingLength();
    if (sessionHasStarted()) {
      return moment.max(moment(meetingStartTime).add(minTotalSeconds, 's'), moment());
    }
    return moment().add(minTotalSeconds, 's');
  };

  /** meetingLength is always total seconds from session start to scheduled end (not remaining time). */
  const sessionHasStarted = (): boolean =>
    cursor >= 0 && meetingStartTime !== undefined && meetingStartTime !== null && meetingStartTime !== '';

  /** HH:mm picker has no date; pin end time to the session day (after start). */
  const anchorEndTimeToSessionDay = (endTime: moment.Moment): moment.Moment => {
    if (!sessionHasStarted()) {
      return endTime;
    }
    const start = moment(meetingStartTime);
    const anchored = endTime
      .clone()
      .year(start.year())
      .month(start.month())
      .date(start.date());
    if (anchored.isBefore(start)) {
      anchored.add(1, 'day');
    }
    return anchored;
  };

  const meetingLengthFromEndTime = (endTime: moment.Moment): number => {
    const anchored = anchorEndTimeToSessionDay(endTime);
    if (sessionHasStarted()) {
      return Math.round(anchored.diff(moment(meetingStartTime), 'seconds', true));
    }
    return Math.round(anchored.diff(moment(), 'seconds', true));
  };

  const meetingEndTimeFromLength = (lengthSeconds: number): moment.Moment => {
    if (sessionHasStarted()) {
      return moment(meetingStartTime).add(lengthSeconds, 's');
    }
    return moment().add(lengthSeconds, 's');
  };

  const remainingMinutesLabel = (): number => {
    if (sessionHasStarted()) {
      const end = moment(tempEndTime, TIME_DISPLAY_FORMAT);
      return Math.max(Math.round(end.diff(moment(), 'seconds') / 60), 0);
    }
    return Math.round(tempMeetingLength / 60);
  };

  useEffect(() => {
    if (!isAdmin) return;

    if (meetingEndTime) {
      setTempEndTime(moment(meetingEndTime).format(TIME_DISPLAY_FORMAT))
    } else {
      setTempEndTime((moment(new Date()).add(meetingLength, "s")).format(TIME_DISPLAY_FORMAT))
    }

    if (meetingLength) {
      setTempMeetingLength(meetingLength)
    }
  }, [settingsMenu])

  /** Before start: refresh derived end-time label in minutes mode (display only; no persist). */
  useEffect(() => {
    if (isEditing || cursor >= 0 || prioritizeEndTime) return;
    setTempEndTime(moment().add(tempMeetingLength || meetingLength, 's').format(TIME_DISPLAY_FORMAT));
  }, [elapsedTimeTrigger, cursor, isEditing, prioritizeEndTime, tempMeetingLength, meetingLength])

  useEffect(()=> {
    setTempMeetingLength(meetingLength)
  }, [meetingLength])


  const showScheduleToast = (message: string): void => {
    setScheduleToastMessage(message);
    setScheduleToastOpen(true);
  };

  const revertTempScheduleFields = (): void => {
    setTempMeetingLength(meetingLength);
    if (meetingEndTime) {
      setTempEndTime(moment(meetingEndTime).format(TIME_DISPLAY_FORMAT));
    } else {
      setTempEndTime(moment().add(meetingLength, 's').format(TIME_DISPLAY_FORMAT));
    }
  };

  const minimumScheduleToastMessage = (): string => {
    const minMinutes = Math.ceil(calculateMinimumMeetingLength() / 60);
    return `The meeting can't end sooner than about ${minMinutes} min based on the current agenda.`;
  };

  type ResolvedSchedule = {
    meetingEndTime: moment.Moment;
    meetingLength: number;
    scheduleClampedToast: string | null;
  };

  /** Shared end-time / length math for Start session and toolbar schedule edits. */
  const resolveScheduleValues = (): ResolvedSchedule | 'invalid' => {
    const minTotalSeconds = calculateMinimumMeetingLength();
    const earliestAllowedEnd = getEarliestAllowedEndTime();
    let scheduleClampedToast: string | null = null;
    const requestedEndTime = anchorEndTimeToSessionDay(moment(tempEndTime, TIME_DISPLAY_FORMAT));

    if (!requestedEndTime.isValid()) {
      return 'invalid';
    }

    let updatedValues = {
      meetingEndTime: requestedEndTime,
      meetingLength: tempMeetingLength || meetingLength,
    };

    if (prioritizeEndTime) {
      updatedValues.meetingEndTime = anchorEndTimeToSessionDay(updatedValues.meetingEndTime);
      updatedValues.meetingLength = meetingLengthFromEndTime(updatedValues.meetingEndTime);
    } else {
      updatedValues.meetingEndTime = meetingEndTimeFromLength(updatedValues.meetingLength);
      if (sessionHasStarted()) {
        updatedValues.meetingLength = Math.round(
          updatedValues.meetingEndTime.diff(moment(meetingStartTime), 'seconds', true)
        );
      } else {
        updatedValues.meetingLength = Math.round(
          updatedValues.meetingEndTime.diff(moment(), 'seconds', true)
        );
      }
    }

    if (updatedValues.meetingLength < minTotalSeconds) {
      scheduleClampedToast = minimumScheduleToastMessage();
      updatedValues.meetingLength = minTotalSeconds;
      updatedValues.meetingEndTime = sessionHasStarted()
        ? moment(meetingStartTime).add(minTotalSeconds, 's')
        : moment().add(minTotalSeconds, 's');
    } else if (prioritizeEndTime && updatedValues.meetingEndTime.isBefore(earliestAllowedEnd)) {
      scheduleClampedToast = minimumScheduleToastMessage();
      updatedValues.meetingEndTime = earliestAllowedEnd.clone();
      updatedValues.meetingLength = minTotalSeconds;
    }

    return {
      meetingEndTime: updatedValues.meetingEndTime,
      meetingLength: updatedValues.meetingLength,
      scheduleClampedToast,
    };
  };

  const applyResolvedScheduleToTempFields = (resolved: ResolvedSchedule): void => {
    setTempEndTime(resolved.meetingEndTime.format(TIME_DISPLAY_FORMAT));
    setTempMeetingLength(resolved.meetingLength);
  };

  const handleOnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (tempEndTime === "") {
      return;
    }

    const resolved = resolveScheduleValues();
    if (resolved === 'invalid') {
      showScheduleToast('Enter a valid end time.');
      revertTempScheduleFields();
      return;
    }

    applyResolvedScheduleToTempFields(resolved);

    const saved = await writeMeetingChange({
      meetingEndTime: resolved.meetingEndTime.toDate(),
      meetingLength: resolved.meetingLength,
    });

    if (!saved) {
      showScheduleToast(
        resolved.scheduleClampedToast ??
          "Can't shorten the meeting to that time—the agenda has no more time to give up."
      );
      revertTempScheduleFields();
      return;
    }

    if (resolved.scheduleClampedToast) {
      showScheduleToast(resolved.scheduleClampedToast);
    }
    setStartMeeting(true);
  };

  const updateMeetingLength = async (): Promise<void> => {
    const resolved = resolveScheduleValues();
    if (resolved === 'invalid') {
      showScheduleToast('Enter a valid end time.');
      revertTempScheduleFields();
      return;
    }

    applyResolvedScheduleToTempFields(resolved);

    const lengthDeltaSeconds = Math.abs(resolved.meetingLength - meetingLength);
    const endTimeChanged =
      !meetingEndTime ||
      moment(meetingEndTime).format(TIME_DISPLAY_FORMAT) !== resolved.meetingEndTime.format(TIME_DISPLAY_FORMAT);

    if (lengthDeltaSeconds < 1 && !endTimeChanged) {
      return;
    }

    const saved = await writeMeetingChange({
      meetingEndTime: resolved.meetingEndTime.toDate(),
      meetingLength: resolved.meetingLength,
    });

    if (!saved) {
      showScheduleToast(
        resolved.scheduleClampedToast ??
          "Can't shorten the meeting to that time—the agenda has no more time to give up."
      );
      revertTempScheduleFields();
    } else if (resolved.scheduleClampedToast) {
      showScheduleToast(resolved.scheduleClampedToast);
    }
  }
  

  const createMeetingControls = () => {
    return (
      <div id="control-buttons">
        {isAdmin && (cursor === -1) && !isArchived && (
          <Button id="start-button" variant="text" disabled={!meetingEditsEnabled} onClick={handleOnSubmit}>
            Start session
          </Button>
        )}
        {isAdmin && (cursor > -1 && cursor < presenters.length + 1) && !isArchived && (
          <div>
            <Button endIcon={<ArrowUpward />} disabled={!meetingEditsEnabled} onClick={() => { setIsPrevButtonPressed(true) }}>Prev</Button>
            <Button endIcon={<ArrowDownward />} disabled={!meetingEditsEnabled} onClick={() => { setIsNextButtonPressed(true) }}>Next</Button>
          </div>
        )}
        {isAdmin && cursor > -1 && !isArchived && (
          <Button variant="text" disabled={!meetingEditsEnabled} onClick={() => { archiveMeeting() }}>
            End session &amp; save
          </Button>
        )}
      </div>
    );
  }
  // =====================================================================================

  // Name Change =========================================================================

  const hanldeMeetingName = () => {
    if (!isArchived) {
      return meetingName
    }
    return `${meetingName} ${moment(meetingEndTime).format("MMM D YYYY")} (Completed)`
  }

  // =====================================================================================

  const handleArchiveMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setArchiveMenu(event.currentTarget);
    retrieveArchivedList()
  };

  const handleArchiveMenuClose = () => {
    setArchiveMenu(null);
  };

  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSettingsMenu(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenu(null);
  };

  const openLiveSession = () => {
    handleArchiveMenuClose();
    setLoading(true);
    loadLiveSession().finally(() => setLoading(false));
  };

  const openHistoricalSession = (sessionId: string) => {
    handleArchiveMenuClose();
    setLoading(true);
    loadHistoricalSession(sessionId).finally(() => setLoading(false));
  };

  const createMeetingName = () => {
    return (
      <Typography variant="h5" id="meetingName">
        {hanldeMeetingName()}
        <IconButton
          onClick={(event) => { handleArchiveMenuOpen(event) }}
          ref={archiveAnchorRef}
          size="large">
          <ArrowDropDownIcon />
        </IconButton>
        <Menu
          id="simple-menu"
          anchorEl={archiveMenu}
          keepMounted
          open={Boolean(archiveMenu)}
          onClose={handleArchiveMenuClose}
        >
          <MenuList>
            <MenuItem id="activeMeetingsMenuItem" onClick={openLiveSession}>
              {viewingSessionId ? 'Current live session' : 'Live session (now)'}
            </MenuItem>
            {archivedMeetingList.length > 0 && (
              <>
                <Divider textAlign="left" style={{ fontSize: '9pt', color: 'grey' }}>PREVIOUS</Divider>
                {archivedMeetingList
                  .sort((a, b) => moment(b.endedAt).diff(moment(a.endedAt)))
                  .map((item) => (
                    <MenuItem key={item.sessionId} onClick={() => openHistoricalSession(item.sessionId)}>
                      {moment(item.endedAt).format('MMM Do, YYYY, h:mm a')}
                    </MenuItem>
                  ))}
              </>
            )}
            {archivedMeetingList.length === 0 && (
              <Typography id="no-archive-message">No previous sessions yet</Typography>
            )}
          </MenuList>
        </Menu>
      </Typography>
    );
  }

  const allowAgendaEditing = !isArchived || Boolean(viewingSessionId);

  const createSettingMenu = () => {
    return (
      <Menu
      // id="simple-menu"
      anchorEl={settingsMenu}
      keepMounted
      open={Boolean(settingsMenu)}
      onClose={handleSettingsMenuClose}
    >
      <MenuList>
        {!viewingSessionId && (
          <MenuItem disabled={!meetingEditsEnabled} onClick={() => { void handleMeetingReset(); handleSettingsMenuClose(); }}>Restart session</MenuItem>
        )}
        {!viewingSessionId && (
          <MenuItem disabled={!meetingEditsEnabled} onClick={() => { setPrepareConfirmOpen(true); handleSettingsMenuClose(); }}>Prepare next session…</MenuItem>
        )}
        {!viewingSessionId && (
          <MenuItem disabled={!meetingEditsEnabled} onClick={() => { clearMeetingAgenda() }}>Clear agenda (all slots)</MenuItem>
        )}
        <MenuItem disabled={!meetingEditsEnabled} onClick={() => { handleMeetingShuffle() }}>Shuffle Presenters</MenuItem>
        {!viewingSessionId && (
          <>
            <Divider textAlign="left" style={{fontSize: '9pt', color: 'grey'}}>DEBUG OPTIONS</Divider>
            {(cursor < 0) && <MenuItem disabled={!meetingEditsEnabled} onClick={() => { handleCreateRandomMeeting() }}>Create Random Meeting </MenuItem>}
            {(cursor < 0) && (
              <FormControlLabel
                labelPlacement='start'
                label="Debug Mode"
                control={
                  <Switch
                    checked={isDebugModeEnabled}
                    disabled={!meetingEditsEnabled}
                    onChange={() => { handleDebug() }}
                  />
                }
              />
            )}
          </>
        )}
        <MenuItem onClick={() => { updateStateFromDatabase() }}>Refresh from server</MenuItem>
        {isAdmin && liveSessionId && (
          <MenuItem onClick={() => { setReplayOpen(true); handleSettingsMenuClose(); }}>Session replay (debug)</MenuItem>
        )}
      </MenuList>
    </Menu>
    )
  }

  const createToolbarIcon = () => {
    return (
      <div style={{ marginLeft: "auto", display:'flex', alignItems:'center' }}>
        {isAdmin &&
          <TextField
            id="editable-value"
            size='small'
            variant="standard"
            disabled={!meetingEditsEnabled}
            type={(prioritizeEndTime)? "time" : "number"}
            onChange= {(event:React.ChangeEvent<HTMLInputElement>) => (prioritizeEndTime)? setTempEndTime(event.target.value): setTempMeetingLength(parseInt(event.target.value)*60)}
            value= {(prioritizeEndTime)? tempEndTime : Math.round(tempMeetingLength/60)}
            onFocus= {()=> {setIsEditing(true)}}
            onBlur= {() => {setIsEditing(false); void updateMeetingLength();}}
            inputProps={{
              step: 300,
              min: (prioritizeEndTime)? getEarliestAllowedEndTime().format(TIME_DISPLAY_FORMAT): calculateMinimumMeetingLength(),
            }}
            InputProps={{
              endAdornment: 
              <InputAdornment position='end'>
                {!prioritizeEndTime && <div>min</div> }
                <IconButton disabled={!meetingEditsEnabled} onClick={() => setIsEditable(!prioritizeEndTime)} size="large">
                  <SwapHoriz style={{ fontSize: '10pt' }}/>
                </IconButton>
                {prioritizeEndTime && <div style={{fontSize: '8pt', lineHeight:'10pt'}}>{remainingMinutesLabel()} min </div>}
                {!prioritizeEndTime && <div style={{fontSize: '8pt', lineHeight:'10pt', display:'flex', alignItems:'center'}}>{moment(tempEndTime, TIME_DISPLAY_FORMAT).format('hh:mm a')}  <Schedule style={{ fontSize: '10pt', marginLeft:'4px' }}/></div>}
              </InputAdornment>,  
              inputProps: {  max: 100, min: 1 } }}
          />
        }
        {isAdmin && allowAgendaEditing &&
          <IconButton
            style={{ marginLeft: isArchived ? "auto" : "" }}
            disabled={!meetingEditsEnabled}
            onClick={(event) => { handleSettingsMenuOpen(event) }}
            ref={settingsAnchorRef}
            size="large">
            <SettingsIcon />
          </IconButton>
        }
        {allowAgendaEditing &&
          <Tooltip title={<Typography fontSize={`12pt`}> {isAdmin ? "Toggle admin off" : "Toggle admin on"}</Typography>} placement="bottom" arrow>
            <IconButton onClick={() => { setIsAdmin(!isAdmin) }} size="large">
              {isAdmin ? <LockOpenIcon /> : <LockIcon />}
            </IconButton>
          </Tooltip>
        }
        {allowAgendaEditing &&
          <IconButton size="large" disabled={!meetingEditsEnabled} onClick={() => { setAddPresenterPopup(true) }}>
            <PersonAddIcon />
          </IconButton>
        }
      </div>
    );
  }


  return (
      <ThemeProvider theme={headerTheme}>
        <AppBar position="static" elevation={0} id="meeting-control-container">
          <Toolbar id="meeting-control-toolbar">
            <div style={{ minWidth:'fit-content'}}>
            {createMeetingName()}
            <Typography variant="caption" display="block" id="session-strip">
              {!meetingEditsEnabled
                ? 'Loading session…'
                : viewingSessionId
                  ? `Completed session · ${moment(meetingEndTime).format('MMM D, YYYY h:mm a')} · editable`
                  : 'Live session'}
            </Typography>
            </div>
            <div style={{ flexGrow: 1, minWidth:'fit-content', display:'flex', justifyContent:'center'}}>
            {!isArchived && createMeetingControls()}
            </div>
            <div style={{minWidth:'fit-content'}}>
            {createToolbarIcon()}
            {createSettingMenu()}
            </div>
            <Dialog open={addPresenterPopup} maxWidth={'lg'}>
              <SelfSubmit
                writeMeetingChange={writeMeetingChange}
                cookies={cookies}
                setCookie={setCookie}
                setAddPresenterPopup={setAddPresenterPopup}
                meetingLength={meetingLength}
                meetingEditsEnabled={meetingEditsEnabled}
              />
            </Dialog>
            <Dialog open={prepareConfirmOpen} onClose={() => setPrepareConfirmOpen(false)}>
              <Typography sx={{ p: 2 }}>
                Clears the live agenda for the next occurrence. Past sessions remain in History.
              </Typography>
              <div id="archiveConfirmationButtons">
                <Button onClick={() => setPrepareConfirmOpen(false)}>Cancel</Button>
                <Button
                  disabled={!meetingEditsEnabled}
                  onClick={() => {
                    setPrepareConfirmOpen(false);
                    prepareNextSession();
                  }}
                >
                  Prepare next session
                </Button>
              </div>
            </Dialog>
            {liveSessionId && (
              <SessionReplay
                open={replayOpen}
                onClose={() => setReplayOpen(false)}
                seriesId={meetingID}
                sessionId={viewingSessionId ?? liveSessionId}
              />
            )}
            <Snackbar
              open={scheduleToastOpen}
              autoHideDuration={6000}
              onClose={() => setScheduleToastOpen(false)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert onClose={() => setScheduleToastOpen(false)} severity="warning" variant="filled" sx={{ width: '100%' }}>
                {scheduleToastMessage}
              </Alert>
            </Snackbar>
          </Toolbar>
        </AppBar>
      </ThemeProvider>

  );

}
export default Header;
